import React from 'react'
import { cn } from '@/utils/cn'
import { IconPlus, IconPencil } from '@tabler/icons-react'
import { TeeButton } from './tee-button'

export interface TeeSectionHeaderProps {
  /**
   * Section title (required for accessibility and backward compatibility)
   */
  title: string

  /**
   * Optional React node rendered inside the h1 in place of the title string.
   * When provided, titleNode is rendered instead of title. title remains
   * required so existing callers and screen-reader tooling always have a
   * meaningful label available.
   */
  titleNode?: React.ReactNode

  /**
   * Description rendered below the title
   */
  description?: React.ReactNode
  
  /**
   * Action button text
   */
  actionLabel?: string
  
  /**
   * Action button click handler
   */
  onAction?: () => void
  
  /**
   * Custom icon for the action button
   */
  actionIcon?: React.ReactNode

  /**
   * Fully custom action rendered in the actions row (before the primary
   * action), for CTAs that need their own sizing or styling
   */
  actionNode?: React.ReactNode
  
  /**
   * Additional CSS classes
   */
  className?: string
  
  /**
   * Secondary action button text
   */
  secondaryActionLabel?: string
  
  /**
   * Secondary action button click handler
   */
  onSecondaryAction?: () => void

  showActionIcon?: boolean

  doneActionLabel?: string
  
  onDoneAction?: () => void

  /**
   * Title edit handler - if provided, shows a pencil icon next to the title
   */
  onTitleEdit?: () => void
}

export const TeeSectionHeader = React.forwardRef<HTMLDivElement, TeeSectionHeaderProps>(
  (
    {
      title,
      titleNode,
      description,
      actionLabel,
      onAction,
      actionIcon = <IconPlus size={20} />,
      actionNode,
      secondaryActionLabel,
      onSecondaryAction,
      className,
      showActionIcon = true,
      doneActionLabel,
      onDoneAction,
      onTitleEdit,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          // Mobile stacks the title block above the CTAs, which then run
          // full-width; from `md` it's the original single row.
          //
          // MERGE NOTE: main added the same stack at the `sm` breakpoint. This
          // branch keeps `md` (tablets get the phone layout, which is what the
          // rest of the mobile work assumes) and keeps main's 44px touch
          // target on the buttons below.
          'w-full bg-[var(--background-card)] flex flex-col items-stretch gap-4 p-4 relative rounded-[8px] shadow-[var(--section-shadow)] border border-[var(--border-default)]',
          'md:flex-row md:items-center md:gap-6 md:p-6',
          className
        )}
      >
        <div className="flex-1 flex flex-col gap-3 min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-heading-h3 md:text-heading-h1 font-extrabold font-[family-name:var(--type-font-family-heading)] text-[var(--text-content-title)] uppercase tracking-wider">
              {titleNode ?? title}
            </h1>
            {onTitleEdit && (
              <div
                onClick={onTitleEdit}
                className="cursor-pointer text-[var(--color-text-gold)]"
              >
                <IconPencil size={28} />
              </div>
            )}
          </div>
          {/* Gold rule beneath the page title (brand signature) */}
          <span aria-hidden="true" className="h-[3px] w-16 bg-[var(--color-gold-500)] rounded-full" />
          {description && (
            <div className="text-body-s text-[var(--text-content-sub-title)]">
              {description}
            </div>
          )}
        </div>

        {/* Actions share one row on mobile. `md:contents` dissolves this
            wrapper from `md` up so the buttons become direct flex children of
            the header again and desktop layout is untouched. */}
        <div className="flex flex-row items-center gap-3 md:contents">
        {secondaryActionLabel && (
          <TeeButton
            onClick={onSecondaryAction}
            variant="secondary"
            size="s"
            text={secondaryActionLabel}
            className="flex-1 min-h-[44px] md:flex-none md:w-auto md:min-h-0 md:min-w-[150px]"
          />
        )}

        {actionNode}

        {actionLabel && (
          <TeeButton
            onClick={onAction}
            variant="primary"
            size="s"
            leftIcon={showActionIcon ? actionIcon : undefined}
            text={actionLabel}
            className="flex-1 min-h-[44px] md:flex-none md:w-auto md:min-h-0 md:min-w-[150px]"
          />
        )}

        {doneActionLabel && (
          <TeeButton
            onClick={onDoneAction}
            variant="secondary"
            size="s"
            text={doneActionLabel}
            className="flex-1 min-h-[44px] md:flex-none md:w-auto md:min-h-0 md:min-w-[100px]"
          />
        )}
        </div>
      </div>
    )
  }
)

TeeSectionHeader.displayName = 'TeeSectionHeader'

export { TeeSectionHeader as default }
